import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/util/cn';

// Horizontal pill rail used to switch between categories on a single
// section. One row of chips, auto-scrolls the active chip into view
// when the selection changes (so the active chip stays visible after
// taps near the rail edges on mobile).
//
// Active chip: filled `bg-primary` with white text.
// Inactive chip: outlined, muted text, hover lift.
//
// Source pattern: extracted from `region/ski-resorts.tsx` Top10Section
// so home Top Resorts + Region Guide share one implementation.

export type CategoryChipItem<TKey extends string = string> = {
  key: TKey;
  label: string;
  /** Optional lucide icon component rendered before the label. */
  icon?: LucideIcon;
  /** Render the icon with fill="currentColor" instead of outline.
   *  Used for the ski-trail-shape categories (Circle = beginner,
   *  Square = intermediate, Diamond = expert) so they read as the
   *  signed difficulty ratings, not generic shapes. */
  iconFilled?: boolean;
};

export type CategoryChipsProps<TKey extends string = string> = {
  items: ReadonlyArray<CategoryChipItem<TKey>>;
  active: TKey;
  onSelect: (key: TKey) => void;
  /** Optional className applied to the outer scroll container. */
  className?: string;
  /** Optional label used by screen readers for the rail. */
  ariaLabel?: string;
};

export const CategoryChips = <TKey extends string = string>({
  items,
  active,
  onSelect,
  className,
  ariaLabel,
}: CategoryChipsProps<TKey>) => {
  const stripRef = React.useRef<HTMLDivElement>(null);
  const activeChipRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    const strip = stripRef.current;
    const chip = activeChipRef.current;
    if (!strip || !chip) return;
    const target = chip.offsetLeft - (strip.clientWidth - chip.offsetWidth) / 2;
    strip.scrollLeft = Math.max(0, target);
  }, [active]);

  return (
    <div
      ref={stripRef}
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'flex flex-nowrap items-center gap-1.5',
        'overflow-x-auto overflow-y-hidden overscroll-x-contain touch-pan-x',
        '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
    >
      {items.map((item) => {
        const isActive = item.key === active;
        const Icon = item.icon;
        return (
          <button
            key={item.key}
            ref={isActive ? activeChipRef : undefined}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(item.key)}
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-semibold transition',
              isActive
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'border border-border bg-background text-muted-foreground hover:border-foreground/30 hover:text-foreground',
            )}
          >
            {Icon && (
              <Icon
                className="h-3.5 w-3.5 shrink-0"
                aria-hidden
                fill={item.iconFilled ? 'currentColor' : 'none'}
              />
            )}
            {item.label}
          </button>
        );
      })}
    </div>
  );
};
